import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import {
  GearSix,
  User,
  Bell,
  Shield,
  GlobeHemisphereWest,
  CheckCircle,
  IdentificationCard,
  CaretRight,
} from '@phosphor-icons/react';

const settingsTexts = {
  de: {
    title: 'Einstellungen',
    subtitle: 'Verwalte deine Kontoeinstellungen',
    account: 'Konto',
    email: 'E-Mail',
    username: 'Benutzername',
    accountType: 'Kontotyp',
    language: 'Sprache',
    languageSubtitle: 'Wähle die Anzeigesprache für WORLDofRADIO',
    currentLanguage: 'Aktuelle Sprache',
    tapToSelect: 'Zum Auswählen tippen',
    notifications: 'Benachrichtigungen',
    notificationsSoon: 'Benachrichtigungseinstellungen kommen bald...',
  },

  en: {
    title: 'Settings',
    subtitle: 'Manage your account preferences',
    account: 'Account',
    email: 'Email',
    username: 'Username',
    accountType: 'Account Type',
    language: 'Language',
    languageSubtitle: 'Choose the display language for WORLDofRADIO',
    currentLanguage: 'Current language',
    tapToSelect: 'Tap to select',
    notifications: 'Notifications',
    notificationsSoon: 'Notification preferences coming soon...',
  },

  no: {
    title: 'Innstillinger',
    subtitle: 'Administrer kontoinnstillingene dine',
    account: 'Konto',
    email: 'E-post',
    username: 'Brukernavn',
    accountType: 'Kontotype',
    language: 'Språk',
    languageSubtitle: 'Velg visningsspråk for WORLDofRADIO',
    currentLanguage: 'Gjeldende språk',
    tapToSelect: 'Trykk for å velge',
    notifications: 'Varsler',
    notificationsSoon: 'Varslingsinnstillinger kommer snart...',
  },

  ko: {
    title: '설정',
    subtitle: '계정 환경설정을 관리하세요',
    account: '계정',
    email: '이메일',
    username: '사용자 이름',
    accountType: '계정 유형',
    language: '언어',
    languageSubtitle: 'WORLDofRADIO 표시 언어를 선택하세요',
    currentLanguage: '현재 언어',
    tapToSelect: '선택하려면 누르세요',
    notifications: '알림',
    notificationsSoon: '알림 설정은 곧 제공됩니다...',
  },

  fi: {
    title: 'Asetukset',
    subtitle: 'Hallitse tilisi asetuksia',
    account: 'Tili',
    email: 'Sähköposti',
    username: 'Käyttäjänimi',
    accountType: 'Tilin tyyppi',
    language: 'Kieli',
    languageSubtitle: 'Valitse WORLDofRADIO:n näyttökieli',
    currentLanguage: 'Nykyinen kieli',
    tapToSelect: 'Valitse napauttamalla',
    notifications: 'Ilmoitukset',
    notificationsSoon: 'Ilmoitusasetukset tulossa pian...',
  },
};

const LegalCard = ({ icon: Icon, title, description, onClick, buttonLabel }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full rounded-2xl border border-white/10 bg-[#070A10]/80 p-5 text-left hover:border-cyan-400/40 hover:bg-white/[0.04] transition-all group"
  >
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-start gap-4 min-w-0">
        <div className="w-12 h-12 rounded-2xl bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center flex-shrink-0">
          <Icon size={24} weight="duotone" className="text-cyan-400" />
        </div>

        <div className="min-w-0">
          <h3 className="text-white font-bold text-lg group-hover:text-cyan-300 transition-colors">
            {title}
          </h3>
          <p className="text-sm text-gray-400 mt-1 leading-relaxed">
            {description}
          </p>
        </div>
      </div>

      <div className="hidden sm:flex items-center gap-2 text-sm font-bold text-gray-500 group-hover:text-cyan-300 transition-colors flex-shrink-0">
        {buttonLabel}
        <CaretRight size={18} />
      </div>
    </div>
  </button>
);

const Settings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language, setLanguage, languages, t } = useLanguage();

  const text = settingsTexts[language] || settingsTexts.en;
  const activeLanguage = languages.find((item) => item.code === language);

  return (
    <div className="min-h-screen bg-[#050505] pb-24 md:pb-0">
      <Navbar />

      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-12">
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <GearSix size={40} weight="duotone" className="text-cyan-400" />

            <h1
              className="text-4xl sm:text-5xl tracking-tighter font-black text-white"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {text.title}
            </h1>
          </div>

          <p className="text-base text-gray-300">{text.subtitle}</p>
        </div>

        <div className="space-y-6">
          <div className="backdrop-filter backdrop-blur-2xl bg-black/60 border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)] rounded-3xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <User size={24} weight="duotone" className="text-cyan-400" />

              <h2
                className="text-2xl tracking-tight font-bold text-white"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {text.account}
              </h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-gray-400">
                  {text.email}
                </label>
                <p className="text-white">{user?.email}</p>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-400">
                  {text.username}
                </label>
                <p className="text-white">@{user?.username}</p>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-400">
                  {text.accountType}
                </label>
                <p className="text-white capitalize">{user?.role}</p>
              </div>
            </div>
          </div>

          <div className="backdrop-filter backdrop-blur-2xl bg-black/60 border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)] rounded-3xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <GlobeHemisphereWest
                size={24}
                weight="duotone"
                className="text-cyan-400"
              />

              <div>
                <h2
                  className="text-2xl tracking-tight font-bold text-white"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {text.language}
                </h2>

                <p className="text-sm text-gray-400 mt-1">
                  {text.languageSubtitle}
                </p>
              </div>
            </div>

            <div className="mb-5 rounded-2xl border border-cyan-400/30 bg-cyan-400/10 px-5 py-4">
              <p className="text-xs font-semibold text-gray-400 mb-1">
                {text.currentLanguage}
              </p>

              <p className="text-cyan-300 font-bold">
                {activeLanguage?.name || 'English'} ({activeLanguage?.label || 'EN'})
              </p>
            </div>

            <div className="rounded-2xl border border-white/10 overflow-hidden bg-[#070A10]/80">
              {languages.map((item, index) => {
                const isActive = language === item.code;

                return (
                  <button
                    key={item.code}
                    type="button"
                    onClick={() => setLanguage(item.code)}
                    className={`w-full flex items-center justify-between px-5 py-4 text-left transition-all ${isActive
                        ? 'bg-cyan-400/10 text-cyan-300'
                        : 'text-gray-300 hover:bg-white/5 hover:text-cyan-300'
                      } ${index !== languages.length - 1
                        ? 'border-b border-white/10'
                        : ''
                      }`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-12 h-8 rounded-full flex items-center justify-center text-xs font-black border ${isActive
                            ? 'border-cyan-400 bg-cyan-400 text-black'
                            : 'border-white/10 bg-white/5 text-gray-400'
                          }`}
                      >
                        {item.label}
                      </div>

                      <div>
                        <div className="text-sm font-bold">{item.name}</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {text.tapToSelect}
                        </div>
                      </div>
                    </div>

                    {isActive && (
                      <CheckCircle
                        size={22}
                        weight="fill"
                        className="text-cyan-400 flex-shrink-0"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="backdrop-filter backdrop-blur-2xl bg-black/60 border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)] rounded-3xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <Bell size={24} weight="duotone" className="text-cyan-400" />

              <h2
                className="text-2xl tracking-tight font-bold text-white"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {text.notifications}
              </h2>
            </div>

            <p className="text-gray-400">{text.notificationsSoon}</p>
          </div>

          <div className="backdrop-filter backdrop-blur-2xl bg-black/60 border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)] rounded-3xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <Shield size={24} weight="duotone" className="text-cyan-400" />

              <h2
                className="text-2xl tracking-tight font-bold text-white"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {t.legalSectionTitle}
              </h2>
            </div>

            <div className="space-y-4">
              <LegalCard
                icon={Shield}
                title={t.privacyLink}
                description={t.privacyCardText}
                buttonLabel={t.legalOpen}
                onClick={() => navigate('/datenschutz')}
              />

              <LegalCard
                icon={IdentificationCard}
                title={t.imprintLink}
                description={t.imprintCardText}
                buttonLabel={t.legalOpen}
                onClick={() => navigate('/impressum')}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Settings;